import React, { useState, useEffect, useRef } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faArchive,
  faSpinner,
  faCheck,
  faTimes,
  faUser,
  faEnvelope,
  faMapMarkerAlt,
  faCalendar,
  faIdCard,
  faLock,
  faUnlock,
  faUsers,
  faCircleCheck,
  faCircleXmark,
  faPause,
  faCreditCard,
  faUniversity,
  faMoneyBillTransfer,
  faCircleExclamation,
  faPhone,
  faChartBar,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ArchiveUserModal from '../../components/ArchiveUserModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import SearchableSelect from '../../components/SearchableSelect';
import ProfileAvatar from '../../components/ProfileAvatar';
import ProfilePhotoPicker from '../../components/ProfilePhotoPicker';

const SupervisorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [supervisor, setSupervisor] = useState(null);
  // Photo chosen while editing; only sent when « Enregistrer » is clicked.
  const [photoFile, setPhotoFile] = useState(null);

  // Roles allowed to POST /supervisors/{id}/photo (the supervisor WRITE group).
  // The camera button itself only appears once « Modifier » is clicked (see canEdit below).
  // resp_controle can open this page but is read-only, so it is deliberately absent.
  const canEditPhoto = ['chef_terrain', 'chef_terrain_superieur', 'admin_it', 'charge_etude',
    'charge_etude_superieur', 'administrateur_general', 'administrateur_general_superieur']
    .includes(currentUser?.role);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [interviewers, setInterviewers] = useState([]);
  const [interviewersLoading, setInterviewersLoading] = useState(false);
  const [surveyStats, setSurveyStats] = useState([]);
  const [surveyStatsLoading, setSurveyStatsLoading] = useState(false);
  const [statsSurveyFilter, setStatsSurveyFilter] = useState(null); // survey_id or null
  const [statsStatusFilter, setStatsStatusFilter] = useState('en_cours'); // en_cours | termine | tous
  const [statsPage, setStatsPage] = useState(1);
  const STATS_PER_PAGE = 10;
  const [vagueModal, setVagueModal] = useState(null); // { survey_title, quota_projects }
  const vagueDismiss = useModalDismiss(() => setVagueModal(null), { enabled: !!vagueModal });

  // Assigné cell click: open the quota. Single project → go directly; multiple vagues → pick one.
  const handleAssignedClick = (s) => {
    const projects = s.quota_projects || [];
    if (projects.length === 0) return;
    if (projects.length === 1) {
      navigate(`/quotas/${projects[0].id}`);
      return;
    }
    setVagueModal({ survey_title: s.survey_title, quota_projects: projects });
  };

  // Réalisé cell click: open F2F productivity prefiltered on this survey + this supervisor.
  const handleRealiseClick = (s) => {
    navigate(`/f2f/productivity?survey_id=${s.survey_id}&supervisor_id=${id}`);
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(String(d).replace(' ', 'T')).toLocaleDateString('fr-FR'); } catch (e) { return String(d).substring(0, 10); }
  };
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [confidentialData, setConfidentialData] = useState(null);
  const [confidentialLoading, setConfidentialLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    status: 'active',
    region: '',
    city_id: '',
    custom_lat: '',
    custom_lng: '',
    custom_location_name: '',
    ville: [],
    payment_method_type: '',
    bank_name: '',
    transfer_service: '',
    payment_name: ''
  });

  // Cities data for edit form
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityDropdownRef = useRef(null);

  useEffect(() => {
    fetchSupervisor();
    fetchInterviewers();
    fetchSurveyStats();
    fetchConfidentialData();
  }, [id]);

  const fetchConfidentialData = async () => {
    try {
      setConfidentialLoading(true);
      const response = await api.get(`/confidential-data/supervisor/${id}?masked=false`);
      if (response.data.success) {
        setConfidentialData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching confidential data:', error);
      setConfidentialData(null);
    } finally {
      setConfidentialLoading(false);
    }
  };

  // Fetch cities and regions when editing starts
  useEffect(() => {
    if (editing) {
      fetchCitiesAndRegions();
    }
  }, [editing]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCitiesAndRegions = async () => {
    try {
      const [citiesResponse, regionsResponse] = await Promise.all([
        api.get('/cities'),
        api.get('/cities/regions')
      ]);
      
      // Handle the API response format { success: true, data: [...] }
      setCities(citiesResponse.data.success ? citiesResponse.data.data : []);
      setRegions(regionsResponse.data.success ? regionsResponse.data.data : []);
    } catch (error) {
      console.error('Error fetching cities and regions:', error);
      // Set empty arrays as fallback
      setCities([]);
      setRegions([]);
    }
  };

  const fetchSupervisor = async () => {
    try {
      setLoading(true);
      console.log('Fetching supervisor with ID:', id);
      console.log('ID type:', typeof id);
      console.log('API URL:', `/supervisors/${id}`);
      
      const response = await api.get(`/supervisors/${id}`);
      console.log('Supervisor response:', response.data);
      setSupervisor(response.data);
      setIsLocked(response.data.is_locked || false);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        status: response.data.status || 'active',
        region: response.data.region || '',
        city_id: response.data.city_id || '',
        custom_lat: response.data.custom_lat || '',
        custom_lng: response.data.custom_lng || '',
        custom_location_name: response.data.custom_location_name || '',
        ville: response.data.ville || [],
        payment_method_type: response.data.payment_method_type || '',
        bank_name: response.data.bank_name || '',
        transfer_service: response.data.transfer_service || '',
        payment_name: response.data.payment_name || ''
      });

      // Set city search and region filter based on current data
      if (response.data.city_name) {
        setCitySearch(response.data.city_name);
        setSelectedRegion(response.data.city_region || '');
        setShowCustomLocation(false);
      } else if (response.data.custom_location_name) {
        setCitySearch(response.data.custom_location_name);
        setShowCustomLocation(true);
      }
    } catch (error) {
      console.error('Error fetching supervisor:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setError(`Erreur lors du chargement du prestataire: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyStats = async () => {
    try {
      setSurveyStatsLoading(true);
      const response = await api.get(`/supervisors/${id}/survey-stats`);
      setSurveyStats(response.data.data || []);
    } catch (error) {
      console.error('Error fetching survey stats:', error);
    } finally {
      setSurveyStatsLoading(false);
    }
  };

  const fetchInterviewers = async () => {
    try {
      setInterviewersLoading(true);
      const response = await api.get(`/interviewers?supervisor_id=${id}&type=f2f&per_page=1000`);
      if (response.data.success) {
        setInterviewers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching interviewers:', error);
    } finally {
      setInterviewersLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUnlock = async () => {
    try {
      setUnlocking(true);
      setError('');
      setSuccess(false);
      
      await api.post(`/supervisors/${id}/unlock-account`);
      setSuccess(true);
      setIsLocked(false);
      setShowUnlockModal(false);
      fetchSupervisor(); // Refresh supervisor data
    } catch (error) {
      console.error('Error unlocking account:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Erreur lors du déverrouillage du compte');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      // Note: changing the email forces the supervisor to re-verify it (backend
      // resets email_verified_at) and syncs to Firebase.
      const updateData = {
        name: formData.name,
        email: formData.email,
        status: formData.status,
        region: formData.region,
        city_id: formData.city_id,
        custom_lat: formData.custom_lat,
        custom_lng: formData.custom_lng,
        custom_location_name: formData.custom_location_name,
        ville: formData.ville,
        payment_method_type: formData.payment_method_type || null,
        bank_name: formData.payment_method_type === 'bank' ? formData.bank_name : null,
        transfer_service: formData.payment_method_type === 'transfer_service' ? formData.transfer_service : null,
        payment_name: formData.payment_name || null
      };
      
      console.log('Sending update data:', updateData);
      await api.put(`/supervisors/${id}`, updateData);

      // The staged photo is sent once the record itself is saved; a failure there must not
      // make a successful update look like it failed.
      if (photoFile) {
        try {
          const form = new FormData();
          form.append('photo', photoFile, 'photo.jpg');
          await api.post(`/supervisors/${id}/photo`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          setErrors({ general: "Les informations ont été enregistrées, mais la photo n'a pas pu l'être." });
        }
        setPhotoFile(null);
      }

      setSuccess(true);
      setEditing(false);
      fetchSupervisor(); // Refresh supervisor data (photo_updated_at included)
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating supervisor:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Erreur lors de la mise à jour du prestataire' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = () => {
    setShowArchiveModal(false);
    navigate('/supervisors', { state: { successMessage: 'Prestataire archivé avec succès' } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, #dc2626 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="animate-spin text-red-600 text-4xl mb-4" 
            />
            <p className="text-gray-600 text-lg">Chargement du prestataire...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, #dc2626 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 text-lg mb-4">{error}</p>
            <button 
              onClick={() => navigate('/supervisors')}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Retour aux Prestataires
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'suspended': return 'text-yellow-600 bg-yellow-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  const formatCoordinate = (coord) => {
    if (!coord) return null;
    const num = Number(coord);
    return isNaN(num) ? null : num.toFixed(6);
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    console.log('City selected:', city);
    setFormData(prev => ({
      ...prev,
      city_id: city.id,
      region: city.region
    }));
    setCitySearch(city.name);
    setShowCustomLocation(false);
    setShowCityDropdown(false);
    console.log('Form data updated with city:', city.id, city.name);
  };

  // Handle custom location toggle
  const handleCustomLocationToggle = () => {
    setShowCustomLocation(!showCustomLocation);
    setShowCityDropdown(false);
    if (!showCustomLocation) {
      setFormData(prev => ({
        ...prev,
        city_id: '',
        region: ''
      }));
      setCitySearch('');
      setSelectedRegion('');
    }
  };

  // Handle region filter change
  const handleRegionChange = (e) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    
    // If a city is selected but doesn't match the new region filter, clear it
    if (formData.city_id && newRegion && Array.isArray(cities)) {
      const selectedCity = cities.find(city => city.id === formData.city_id);
      if (selectedCity && selectedCity.region !== newRegion) {
        setFormData(prev => ({
          ...prev,
          city_id: '',
          region: ''
        }));
        setCitySearch('');
      }
    }
  };

  // Filter cities based on search and region
  const filteredCities = Array.isArray(cities) ? cities.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(citySearch.toLowerCase());
    const matchesRegion = !selectedRegion || city.region === selectedRegion;
    return matchesSearch && matchesRegion;
  }) : [];

  // Only roles that can open the quota builder get clickable "Assigné" cells (e.g. resp_controle can't).
  const canOpenQuota = ['charge_etude', 'charge_etude_superieur', 'chef_terrain', 'chef_terrain_superieur', 'administrateur_general', 'administrateur_general_superieur'].includes(currentUser?.role);

  // Survey-stats filter (applies to the whole dataset) + client-side pagination.
  // Une enquête tracker produit une ligne par vague : dédoublonner pour le filtre.
  const statsSurveyOptions = Array.from(new Map(surveyStats.map(s => [s.survey_id, { id: s.survey_id, title: s.survey_title }])).values());
  // Par défaut on masque les lignes terminées : avec le temps elles s'accumulent et noient
  // le travail en cours. Les compteurs sont calculés avant le filtre de statut, sinon
  // l'onglet actif afficherait toujours le total de lui-même.
  const statsBySurvey = statsSurveyFilter
    ? surveyStats.filter(s => String(s.survey_id) === String(statsSurveyFilter))
    : surveyStats;
  const statusCounts = {
    en_cours: statsBySurvey.filter(s => !s.is_finished).length,
    termine: statsBySurvey.filter(s => s.is_finished).length,
    tous: statsBySurvey.length,
  };
  const filteredStats = statsStatusFilter === 'tous'
    ? statsBySurvey
    : statsBySurvey.filter(s => (statsStatusFilter === 'termine' ? s.is_finished : !s.is_finished));
  const statsTotalPages = Math.max(1, Math.ceil(filteredStats.length / STATS_PER_PAGE));
  const statsCurrentPage = Math.min(statsPage, statsTotalPages);
  const pagedStats = filteredStats.slice((statsCurrentPage - 1) * STATS_PER_PAGE, statsCurrentPage * STATS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, #dc2626 1px, transparent 1px)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex items-center space-x-4">
              {/* In edit mode the photo lives in the form below, not next to the title */}
              {!editing && (
                <ProfileAvatar
                  personType="supervisors"
                  personId={supervisor?.id}
                  name={supervisor?.name}
                  photoUpdatedAt={supervisor?.photo_updated_at}
                  size="lg"
                  className="ring-2 ring-white shadow-md"
                />
              )}
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">
                  {editing ? 'Modifier le Prestataire' : 'Détails du Prestataire'}
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  {supervisor?.ref} - {supervisor?.name}
                </p>
              </div>
            </div>
            
            {!editing && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:flex-shrink-0">
                {currentUser?.role === 'admin_it' && (
                  <button
                    onClick={() => setShowUnlockModal(true)}
                    disabled={!isLocked || unlocking}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base ${
                      isLocked && !unlocking
                        ? 'bg-orange-600 text-white hover:bg-orange-700'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    }`}
                    title={isLocked ? 'Déverrouiller le compte' : 'Le compte n\'est pas verrouillé'}
                  >
                    <FontAwesomeIcon icon={isLocked ? faUnlock : faLock} className="w-4 h-4" />
                    <span className="whitespace-nowrap">
                      {unlocking ? 'Déverrouillage...' : isLocked ? 'Déverrouiller' : 'Déverrouillé'}
                    </span>
                  </button>
                )}
                {!['administrateur_general', 'charge_etude', 'charge_etude_superieur', 'resp_controle'].includes(currentUser?.role) && (
                  <button
                    onClick={() => setEditing(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                    <span className="whitespace-nowrap">Modifier</span>
                  </button>
                )}
                {supervisor?.map_lat && supervisor?.map_lng && formatCoordinate(supervisor.map_lat) && formatCoordinate(supervisor.map_lng) && (
                  <button
                    onClick={() => navigate('/supervisors-map')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4" />
                    <span className="whitespace-nowrap">Voir sur la carte</span>
                  </button>
                )}
                {!['administrateur_general', 'charge_etude', 'charge_etude_superieur', 'resp_controle'].includes(currentUser?.role) && (
                  <button
                    onClick={() => setShowArchiveModal(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <FontAwesomeIcon icon={faArchive} className="w-4 h-4" />
                    <span className="whitespace-nowrap">Archiver</span>
                  </button>
                )}
              </div>
            )}

            {editing && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:flex-shrink-0">
                <button
                  onClick={() => {
                    setEditing(false);
                    setPhotoFile(null);
                    setErrors({});
                    setFormData({
                      name: supervisor.name || '',
                      email: supervisor.email || '',
                      status: supervisor.status || 'active',
                      region: supervisor.region || '',
                      city_id: supervisor.city_id || '',
                      custom_lat: supervisor.custom_lat || '',
                      custom_lng: supervisor.custom_lng || '',
                      custom_location_name: supervisor.custom_location_name || '',
                      ville: supervisor.ville || [],
                      payment_method_type: supervisor.payment_method_type || '',
                      bank_name: supervisor.bank_name || '',
                      transfer_service: supervisor.transfer_service || '',
                      payment_name: supervisor.payment_name || ''
                    });

                    // Reset city selection state
                    if (supervisor.city_name) {
                      setCitySearch(supervisor.city_name);
                      setSelectedRegion(supervisor.city_region || '');
                      setShowCustomLocation(false);
                    } else if (supervisor.custom_location_name) {
                      setCitySearch(supervisor.custom_location_name);
                      setShowCustomLocation(true);
                    } else {
                      setCitySearch('');
                      setSelectedRegion('');
                      setShowCustomLocation(false);
                    }
                    setShowCityDropdown(false);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                  <span className="whitespace-nowrap">Annuler</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                  )}
                  <span className="whitespace-nowrap">{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
                </button>
              </div>
            )}
          </div>

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">Prestataire mis à jour avec succès</p>
            </div>
          )}

          {errors.general && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{errors.general}</p>
            </div>
          )}
        </div>

        {/* Supervisor Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faUser} className="text-red-600 mr-2" />
              Informations de base
            </h3>
            
            <div className="space-y-4">
              {/* Photo — staged here, uploaded on « Enregistrer » */}
              {editing && canEditPhoto && (
                <ProfilePhotoPicker
                  label="Photo du prestataire"
                  onSelect={setPhotoFile}
                  personType="supervisors"
                  personId={supervisor?.id}
                  name={supervisor?.name}
                  photoUpdatedAt={supervisor?.photo_updated_at}
                  hint="JPG, PNG ou WEBP — 5 Mo maximum. Enregistrée avec le formulaire."
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                ) : (
                  <p className="text-gray-900">{supervisor?.name}</p>
                )}
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                ) : (
                  <p className="text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 mr-2" />
                    {supervisor?.email}
                    {supervisor?.email_verified && (
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="text-green-500 ml-2"
                        title="Email vérifié"
                      />
                    )}
                  </p>
                )}
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                {editing ? (
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="active">Actif</option>
                    <option value="suspended">Suspendu</option>
                    <option value="inactive">Inactif</option>
                  </select>
                ) : (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(supervisor?.status)}`}>
                    {getStatusText(supervisor?.status)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600 mr-2" />
              Informations géographiques
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Région
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                ) : (
                  <p className="text-gray-900">{supervisor?.region || '-'}</p>
                )}
                {editing && (
                  <p className="mt-1 text-xs text-gray-500">La région est automatiquement définie lors de la sélection de la ville</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville assignée
                </label>
                {editing ? (
                  <div>
                    {/* City Search Input */}
                    <div className="relative mb-2" ref={cityDropdownRef}>
                      <input
                        type="text"
                        value={citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setShowCityDropdown(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowCityDropdown(citySearch.length > 0)}
                        onBlur={() => {
                          // Delay hiding to allow click on dropdown items
                          setTimeout(() => setShowCityDropdown(false), 150);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setShowCityDropdown(false);
                          }
                        }}
                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                          showCustomLocation ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Rechercher une ville..."
                        disabled={showCustomLocation}
                      />
                      
                      {/* City Dropdown */}
                      {showCityDropdown && citySearch && !showCustomLocation && filteredCities.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredCities.map((city) => (
                            <button
                              key={city.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input blur
                                handleCitySelect(city);
                                setShowCityDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 cursor-pointer"
                            >
                              <div className="font-medium text-gray-900">{city.name}</div>
                              <div className="text-sm text-gray-500">{city.region}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Region Filter */}
                    <div className="mb-4">
                      <select
                        value={selectedRegion}
                        onChange={handleRegionChange}
                        className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors ${
                          showCustomLocation ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        disabled={showCustomLocation}
                      >
                        <option value="">Toutes les régions</option>
                        {regions.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Location Toggle */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={showCustomLocation}
                          onChange={handleCustomLocationToggle}
                          className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Utiliser un emplacement personnalisé</span>
                      </label>
                    </div>

                    {/* Custom Location Fields */}
                    {showCustomLocation && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom de l'emplacement
                          </label>
                          <input
                            type="text"
                            value={formData.custom_location_name}
                            onChange={(e) => setFormData({ ...formData, custom_location_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                            placeholder="Nom de l'emplacement personnalisé"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Latitude
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={formData.custom_lat}
                              onChange={(e) => setFormData({ ...formData, custom_lat: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                              placeholder="Latitude"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Longitude
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={formData.custom_lng}
                              onChange={(e) => setFormData({ ...formData, custom_lng: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                              placeholder="Longitude"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Selected City Display */}
                    {formData.city_id && !showCustomLocation && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                          {citySearch} - {formData.region}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, city_id: '', region: '' }));
                            setCitySearch('');
                            setSelectedRegion('');
                          }}
                          className="text-red-600 hover:text-red-800 text-sm underline"
                        >
                          Changer
                        </button>
                      </div>
                    )}

                    {/* Legacy ville field */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Villes (legacy)
                      </label>
                      <input
                        type="text"
                        value={Array.isArray(formData.ville) ? formData.ville.join(', ') : formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value.split(',').map(v => v.trim()) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        placeholder="Séparer les villes par des virgules"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* New city information */}
                    {supervisor?.city_name ? (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 font-medium">{supervisor.city_name}</p>
                        <p className="text-green-600 text-sm">{supervisor.city_region}</p>
                        {supervisor.map_lat && supervisor.map_lng && formatCoordinate(supervisor.map_lat) && formatCoordinate(supervisor.map_lng) && (
                          <p className="text-green-600 text-xs mt-1">
                            📍 Coordonnées: {formatCoordinate(supervisor.map_lat)}, {formatCoordinate(supervisor.map_lng)}
                          </p>
                        )}
                      </div>
                    ) : supervisor?.custom_location_name ? (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-800 font-medium">{supervisor.custom_location_name}</p>
                        {supervisor.custom_lat && supervisor.custom_lng && formatCoordinate(supervisor.custom_lat) && formatCoordinate(supervisor.custom_lng) && (
                          <p className="text-blue-600 text-xs mt-1">
                            📍 Coordonnées: {formatCoordinate(supervisor.custom_lat)}, {formatCoordinate(supervisor.custom_lng)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">Aucune ville assignée</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Location Details (if available) */}
          {(supervisor?.zone_locales || supervisor?.formations || supervisor?.notes) && (
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600 mr-2" />
                Détails de localisation
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {supervisor?.zone_locales && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zones locales
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(supervisor.zone_locales) ? (
                        supervisor.zone_locales.map((zone, index) => (
                          <span key={index} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {typeof zone === 'object' && zone !== null ? (zone.name || JSON.stringify(zone)) : zone}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {typeof supervisor.zone_locales === 'object' && supervisor.zone_locales !== null ? (supervisor.zone_locales.name || JSON.stringify(supervisor.zone_locales)) : supervisor.zone_locales}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {supervisor?.formations && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Formations
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(supervisor.formations) ? (
                        supervisor.formations.map((formation, index) => (
                          <span key={index} className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            {typeof formation === 'object' && formation !== null ? (formation.name || JSON.stringify(formation)) : formation}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          {typeof supervisor.formations === 'object' && supervisor.formations !== null ? (supervisor.formations.name || JSON.stringify(supervisor.formations)) : supervisor.formations}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {supervisor?.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(supervisor.notes) ? (
                        supervisor.notes.map((note, index) => (
                          <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                            {typeof note === 'object' && note !== null ? (note.name || JSON.stringify(note)) : note}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                          {typeof supervisor.notes === 'object' && supervisor.notes !== null ? (supervisor.notes.name || JSON.stringify(supervisor.notes)) : supervisor.notes}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faIdCard} className="text-red-600 mr-2" />
              Informations supplémentaires
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Référence
                </label>
                <p className="text-gray-900 font-mono">{supervisor?.ref}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                {confidentialLoading ? (
                  <p className="text-gray-500 flex items-center">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Chargement...
                  </p>
                ) : (
                  <p className="text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faPhone} className="text-gray-400 mr-2" />
                    {confidentialData?.phone || <span className="text-gray-400 italic">Non renseigné</span>}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Créé par
                </label>
                <p className="text-gray-900">{supervisor?.created_by || '-'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de création
                </label>
                <p className="text-gray-900 flex items-center">
                  <FontAwesomeIcon icon={faCalendar} className="text-gray-400 mr-2" />
                  {supervisor?.created_at ? new Date(supervisor.created_at).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Method Information */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faCreditCard} className="text-red-600 mr-2" />
              Informations de Paiement
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Méthode de paiement
                </label>
                {editing ? (
                  <select
                    value={formData.payment_method_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({
                        ...formData,
                        payment_method_type: newType,
                        bank_name: newType === 'bank' ? formData.bank_name : '',
                        transfer_service: newType === 'transfer_service' ? formData.transfer_service : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="">Non configuré</option>
                    <option value="bank">Versement bancaire</option>
                    <option value="transfer_service">Service de Transfert</option>
                    <option value="cash">Espèces</option>
                  </select>
                ) : (
                  <p className="text-gray-900 flex items-center">
                    <FontAwesomeIcon
                      icon={
                        supervisor?.payment_method_type === 'bank' ? faUniversity :
                        supervisor?.payment_method_type === 'transfer_service' ? faMoneyBillTransfer :
                        faCreditCard
                      }
                      className="text-gray-400 mr-2"
                    />
                    {supervisor?.payment_method_type === 'bank' ? 'Versement bancaire' :
                     supervisor?.payment_method_type === 'transfer_service' ? 'Service de Transfert' :
                     supervisor?.payment_method_type === 'cash' ? 'Espèces' :
                     'Non configuré'}
                  </p>
                )}
              </div>

              {/* Payment Name - show when a payment method is selected */}
              {(editing ? formData.payment_method_type : supervisor?.payment_method_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom pour le paiement
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.payment_name}
                      onChange={(e) => setFormData({ ...formData, payment_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                      placeholder={supervisor?.name || 'Nom du bénéficiaire'}
                    />
                  ) : (
                    <p className="text-gray-900 flex items-center">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                      {supervisor?.payment_name || supervisor?.name || '-'}
                    </p>
                  )}
                </div>
              )}

              {/* Bank Name - only show for bank payment type */}
              {(editing ? formData.payment_method_type === 'bank' : supervisor?.payment_method_type === 'bank') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banque
                  </label>
                  {editing ? (
                    <select
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    >
                      <option value="">Sélectionner une banque</option>
                      <option value="Al Akhdar Bank">Al Akhdar Bank (AAB)</option>
                      <option value="Al Barid Bank">Al Barid Bank</option>
                      <option value="Arab Bank PLC">Arab Bank PLC</option>
                      <option value="Attijariwafa Bank">Attijariwafa Bank</option>
                      <option value="Banco Sabadell">Banco Sabadell</option>
                      <option value="Bank Al-Amal">Bank Al-Amal</option>
                      <option value="Bank Al Yousr">Bank Al Yousr</option>
                      <option value="Bank Al-Tamweel wa Al-Inma">Bank Al-Tamweel wa Al-Inma</option>
                      <option value="Bank As-Safa">Bank As-Safa</option>
                      <option value="Bank of Africa">Bank of Africa</option>
                      <option value="Banque Populaire">Banque Populaire</option>
                      <option value="BMCI">Banque Marocaine pour le Commerce et l'Industrie (BMCI)</option>
                      <option value="Caixa Bank">Caixa Bank</option>
                      <option value="CIH Bank">CIH Bank</option>
                      <option value="Citibank Morocco">Citibank Morocco</option>
                      <option value="Crédit Agricole du Maroc">Crédit Agricole du Maroc (CAM)</option>
                      <option value="Crédit du Maroc">Crédit du Maroc</option>
                      <option value="Umnia Bank">Umnia Bank</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 flex items-center">
                      <FontAwesomeIcon icon={faUniversity} className="text-blue-600 mr-2" />
                      {supervisor?.bank_name || '-'}
                    </p>
                  )}
                </div>
              )}

              {/* Transfer Service - only show for transfer_service payment type */}
              {(editing ? formData.payment_method_type === 'transfer_service' : supervisor?.payment_method_type === 'transfer_service') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service de transfert
                  </label>
                  {editing ? (
                    <select
                      value={formData.transfer_service}
                      onChange={(e) => setFormData({ ...formData, transfer_service: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    >
                      <option value="">Sélectionner un service</option>
                      <option value="Barid Cash">Barid Cash</option>
                      <option value="Wafacash">Wafacash</option>
                      <option value="Western Union">Western Union</option>
                      <option value="MoneyGram">MoneyGram</option>
                      <option value="Cash Plus">Cash Plus</option>
                    </select>
                  ) : (
                    <p className="text-gray-900 flex items-center">
                      <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-green-600 mr-2" />
                      {supervisor?.transfer_service || '-'}
                    </p>
                  )}
                </div>
              )}

              {/* Cash payment info */}
              {(editing ? formData.payment_method_type === 'cash' : supervisor?.payment_method_type === 'cash') && (
                <div className="md:col-span-2">
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <FontAwesomeIcon icon={faCircleExclamation} className="text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm text-blue-800">
                        Le prestataire doit se rendre chez Immersion pour recevoir le paiement en espèces.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Per-survey stats: assigned quota vs realised vs validated (control) */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 lg:col-span-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faChartBar} className="text-red-600 mr-2" />
                Statistiques par enquête
              </h3>
              {surveyStats.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5 self-start">
                    {[
                      { key: 'en_cours', label: 'En cours' },
                      { key: 'termine', label: 'Terminé' },
                      { key: 'tous', label: 'Tous' },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => { setStatsStatusFilter(tab.key); setStatsPage(1); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                          statsStatusFilter === tab.key
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {tab.label} <span className="text-gray-400 ml-0.5">({statusCounts[tab.key]})</span>
                      </button>
                    ))}
                  </div>
                  <div className="w-full sm:w-64 text-sm">
                    <SearchableSelect
                      options={statsSurveyOptions}
                      selectedValue={statsSurveyFilter}
                      onChange={(id) => { setStatsSurveyFilter(id); setStatsPage(1); }}
                      getOptionLabel={(o) => o.title}
                      showOptionMeta={false}
                      placeholder="Toutes les enquêtes"
                      searchPlaceholder="Rechercher une enquête..."
                      triggerClassName="px-3 py-2"
                    />
                  </div>
                </div>
              )}
            </div>
            {surveyStatsLoading ? (
              <div className="flex justify-center py-6">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-2xl" />
              </div>
            ) : filteredStats.length === 0 ? (
              <p className="text-center text-gray-500 py-6 text-sm">
                {surveyStats.length === 0
                  ? 'Aucune donnée pour ce prestataire.'
                  : (statsStatusFilter === 'en_cours' && statusCounts.termine > 0
                    // Cas courant : tout est terminé. Sans ce message on afficherait « aucun
                    // résultat » alors que les lignes existent, simplement masquées par défaut.
                    ? `Aucune enquête en cours. ${statusCounts.termine} ligne${statusCounts.termine > 1 ? 's' : ''} terminée${statusCounts.termine > 1 ? 's' : ''} — voir « Terminé » ou « Tous ».`
                    : 'Aucune enquête ne correspond au filtre.')}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                        <th className="py-2 pr-3">Enquête</th>
                        <th className="py-2 px-3 text-center" title="Quota total assigné au prestataire">Assigné (quota)</th>
                        <th className="py-2 px-3 text-center" title="Complétions réalisées (lui + ses enquêteurs)">Réalisé</th>
                        <th className="py-2 px-3 text-center" title="Soumissions validées au contrôle">Validé (contrôle)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagedStats.map((s) => (
                        <tr key={`${s.survey_id}-${s.wave_id ?? 'all'}`} className="hover:bg-gray-50">
                          <td className="py-2 pr-3 font-medium text-gray-900">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{s.survey_title}</span>
                              {s.is_finished && (
                                <span
                                  title="Quota terminé : les chiffres de cette ligne sont définitifs (la synchronisation ne tourne plus)."
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 uppercase tracking-wide"
                                >
                                  Terminé
                                </span>
                              )}
                            </div>
                            {s.wave_label && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {s.wave_label}
                                {s.date_debut && s.date_fin ? ` · ${fmtDate(s.date_debut)} → ${fmtDate(s.date_fin)}` : ''}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {canOpenQuota && s.quota_projects && s.quota_projects.length > 0 ? (
                              <button
                                onClick={() => handleAssignedClick(s)}
                                className="font-medium text-red-600 hover:text-red-800 hover:underline"
                                title="Ouvrir le quota"
                              >
                                {s.assigned}
                              </button>
                            ) : (
                              <span className="text-gray-800">{s.assigned}</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {/* Quota terminé sans productivité : la synchronisation ne tourne plus,
                                le chiffre n'est pas récupérable. Afficher « 0 » laisserait croire
                                qu'aucun travail n'a été fait — on affiche l'absence de chiffre, et
                                on neutralise le lien qui mènerait à une page de productivité vide. */}
                            {s.is_finished && s.completed === 0 ? (
                              <span
                                className="text-gray-400 cursor-default"
                                title="Quota terminé : la productivité n'est plus synchronisée, aucun chiffre disponible pour cette période."
                              >
                                —
                              </span>
                            ) : (
                              <button
                                onClick={() => handleRealiseClick(s)}
                                title="Voir la productivité"
                                className={`font-semibold hover:underline ${
                                  s.completed > s.assigned
                                    ? 'text-red-600 hover:text-red-800'
                                    : (s.completed === s.assigned && s.assigned > 0 ? 'text-green-600 hover:text-green-800' : 'text-gray-800 hover:text-gray-900')
                                }`}
                              >
                                {s.completed}
                              </button>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center text-gray-800">{s.validated}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                        <td className="py-2 pr-3">Total{statsSurveyFilter ? '' : ` (${filteredStats.length} ligne${filteredStats.length > 1 ? 's' : ''})`}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.assigned, 0)}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.completed, 0)}</td>
                        <td className="py-2 px-3 text-center">{filteredStats.reduce((a, s) => a + s.validated, 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {statsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-500">Page {statsCurrentPage} / {statsTotalPages}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setStatsPage(p => Math.max(1, p - 1))}
                        disabled={statsCurrentPage <= 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className="text-xs" /> Précédent
                      </button>
                      <button
                        onClick={() => setStatsPage(p => Math.min(statsTotalPages, p + 1))}
                        disabled={statsCurrentPage >= statsTotalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        Suivant <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Interviewers Section */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faUsers} className="text-red-600 mr-2" />
                Enquêteurs ({interviewers.length})
              </h3>
            </div>
            
            {interviewersLoading ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 text-2xl" />
                <p className="mt-2 text-gray-600">Chargement des enquêteurs...</p>
              </div>
            ) : interviewers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Référence
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Note Contrôle
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {interviewers.map((interviewer) => (
                      <tr 
                        key={interviewer.id} 
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/interviewers/${interviewer.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {interviewer.ref}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {interviewer.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {interviewer.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {interviewer.zone ? (
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              interviewer.zone === 'rural' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {interviewer.zone === 'rural' ? 'Rural' : 'Urbain'}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon 
                              icon={
                                interviewer.status === 'active' 
                                  ? faCircleCheck 
                                  : interviewer.status === 'suspended'
                                  ? faPause
                                  : faCircleXmark
                              }
                              className={
                                interviewer.status === 'active' 
                                  ? 'text-green-600' 
                                  : interviewer.status === 'suspended'
                                  ? 'text-red-600'
                                  : 'text-yellow-600'
                              }
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {interviewer.status === 'active' ? 'Actif' :
                               interviewer.status === 'suspended' ? 'Suspendu' :
                               'Inactif'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {interviewer.controllers_rating != null ? (() => {
                            const avg = (parseFloat(interviewer.controllers_rating) / 100) * 5;
                            return (
                              <span className={`font-semibold ${
                                avg >= 4 ? 'text-green-600' :
                                avg >= 3 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {avg.toFixed(1)}/5
                              </span>
                            );
                          })() : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 mb-3" />
                <p>Aucun enquêteur assigné</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Archive Modal */}
      {showArchiveModal && (
        <ArchiveUserModal
          user={supervisor}
          isOpen={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          onArchive={handleArchive}
          archiveEndpoint={`/supervisors/${id}`}
          archiveMethod="DELETE"
          consequences={[
            'Son compte sera désactivé : il ne pourra plus se connecter.',
            'Son historique est conservé (contrôles F2F le concernant, quotas, paiements).',
            'Ses enquêteurs ne sont pas archivés et gardent leur rattachement.',
            'Vous pourrez le restaurer à tout moment depuis la page des archives.',
          ]}
        />
      )}
      
      <ConfirmationModal
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        onConfirm={handleUnlock}
        title="Déverrouiller le compte"
        message="Êtes-vous sûr de vouloir déverrouiller ce compte ? Les tentatives de connexion échouées seront réinitialisées."
        confirmText="Déverrouiller"
        cancelText="Annuler"
        confirmColor="bg-orange-600 hover:bg-orange-700"
        loading={unlocking}
      />

      {/* Vague picker: which quota (wave) to open for a tracker survey */}
      {vagueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...vagueDismiss}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Choisir une vague ({vagueModal.quota_projects.length})</h3>
              <button onClick={() => setVagueModal(null)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0">
              <p className="text-sm text-gray-600 mb-3">{vagueModal.survey_title} — sélectionnez le quota à ouvrir :</p>
              <ul className="divide-y divide-gray-100">
                {vagueModal.quota_projects.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900">{p.wave_label || `Quota #${p.id}`}</div>
                      <div className="text-xs text-gray-500">
                        {p.date_debut || p.date_fin ? `${fmtDate(p.date_debut)} → ${fmtDate(p.date_fin)}` : 'Aucune période'}
                      </div>
                    </div>
                    <button
                      onClick={() => { setVagueModal(null); navigate(`/quotas/${p.id}`); }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex-shrink-0"
                    >
                      Ouvrir
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button onClick={() => setVagueModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorDetail;
